import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { 
  Package, Plus, Search, ArrowLeft, MapPin, 
  Clock, Truck, BarChart3, Users, Upload, Eye, UserPlus,
  Edit, FileText, Trash2, Download, Paperclip, AlertTriangle, Camera, Image, FileDown, CheckSquare
} from "lucide-react";

const LOGO_URL = "/nassau-logo.png";

const StatusBadge = ({ status }) => {
  const styles = {
    pending: "bg-amber-100 text-amber-800 border-amber-200",
    in_progress: "bg-blue-100 text-blue-800 border-blue-200",
    completed: "bg-emerald-100 text-emerald-800 border-emerald-200",
    failed: "bg-red-100 text-red-800 border-red-200"
  };
  
  const labels = {
    pending: "Pending",
    in_progress: "In Progress",
    completed: "Completed",
    failed: "Failed"
  };
  
  return (
    <Badge variant="outline" className={`${styles[status]} font-medium`}>
      {labels[status]}
    </Badge>
  );
};

const JobTypeBadge = ({ type }) => {
  const isCollection = type === "collection";
  return (
    <Badge 
      variant="outline" 
      className={`font-medium ${isCollection ? "bg-purple-100 text-purple-800 border-purple-200" : "bg-cyan-100 text-cyan-800 border-cyan-200"}`}
    >
      {isCollection ? "Collection" : "Delivery"}
    </Badge>
  );
};

export default function JobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [jobTypeFilter, setJobTypeFilter] = useState("all");
  const [customerFilter, setCustomerFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [photosDialogOpen, setPhotosDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [newCustomerDialogOpen, setNewCustomerDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState("");
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    job_type: "delivery",
    customer_id: "",
    delivery_address: "",
    delivery_date: new Date().toISOString().split('T')[0],
    time_window: "",
    items_description: "",
    special_instructions: "",
    assigned_driver_id: ""
  });
  const [editFormData, setEditFormData] = useState({
    job_type: "delivery",
    customer_id: "",
    delivery_address: "",
    delivery_date: "",
    time_window: "",
    items_description: "",
    special_instructions: "",
    assigned_driver_id: "",
    status: ""
  });
  const [newCustomerData, setNewCustomerData] = useState({
    name: "",
    address: "",
    city: "",
    county: "",
    postcode: "",
    country: "",
    email: "",
    phone: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [pendingDocuments, setPendingDocuments] = useState([]);
  const createJobFileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [statusFilter, jobTypeFilter, customerFilter, dateFrom, dateTo]);

  const fetchData = async () => {
    try {
      const [jobsRes, customersRes, driversRes] = await Promise.all([
        api.get("/jobs"),
        api.get("/customers"),
        api.get("/users/drivers")
      ]);
      setJobs(jobsRes.data);
      setCustomers(customersRes.data);
      setDrivers(driversRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (jobTypeFilter !== "all") params.job_type = jobTypeFilter;
      if (customerFilter !== "all") params.customer_id = customerFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const res = await api.get("/jobs", { params });
      setJobs(res.data);
    } catch (error) {
      console.error("Failed to fetch jobs", error);
    }
  };

  const clearFilters = () => {
    setStatusFilter("all");
    setJobTypeFilter("all");
    setCustomerFilter("all");
    setDateFrom("");
    setDateTo("");
    setSearchTerm("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.delivery_address || !formData.delivery_date) {
      toast.error("Customer, address, and date are required");
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await api.post("/jobs", formData);
      const newJobId = response.data.id;
      
      // Upload any pending documents
      if (pendingDocuments.length > 0) {
        for (const file of pendingDocuments) {
          const docFormData = new FormData();
          docFormData.append("file", file);
          try {
            await api.post(`/jobs/${newJobId}/documents`, docFormData, {
              headers: { "Content-Type": "multipart/form-data" }
            });
          } catch (docError) {
            console.error("Failed to upload document:", docError);
          }
        }
      }
      
      toast.success("Job created successfully");
      setDialogOpen(false);
      setFormData({
        job_type: "delivery",
        customer_id: "",
        delivery_address: "",
        delivery_date: new Date().toISOString().split('T')[0],
        time_window: "",
        items_description: "",
        special_instructions: "",
        assigned_driver_id: ""
      });
      setPendingDocuments([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create job");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePendingDocumentAdd = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingDocuments([...pendingDocuments, ...files]);
    if (createJobFileInputRef.current) {
      createJobFileInputRef.current.value = "";
    }
  };

  const removePendingDocument = (index) => {
    setPendingDocuments(pendingDocuments.filter((_, i) => i !== index));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!selectedJob) return;
    
    setSubmitting(true);
    try {
      // Convert __unassigned__ back to empty string
      const submitData = {
        ...editFormData,
        assigned_driver_id: editFormData.assigned_driver_id === "__unassigned__" ? "" : editFormData.assigned_driver_id
      };
      await api.put(`/jobs/${selectedJob.id}`, submitData);
      toast.success("Job updated successfully");
      setEditDialogOpen(false);
      setSelectedJob(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update job");
    } finally {
      setSubmitting(false);
    }
  };

  const openEditDialog = (job) => {
    setSelectedJob(job);
    setEditFormData({
      job_type: job.job_type || "delivery",
      customer_id: job.customer_id,
      delivery_address: job.delivery_address,
      delivery_date: job.delivery_date,
      time_window: job.time_window || "",
      items_description: job.items_description || "",
      special_instructions: job.special_instructions || "",
      assigned_driver_id: job.assigned_driver_id || "__unassigned__",
      status: job.status
    });
    setEditDialogOpen(true);
  };

  const openDocumentsDialog = (job) => {
    setSelectedJob(job);
    setDocumentsDialogOpen(true);
  };

  const openDeleteDialog = (job) => {
    setSelectedJob(job);
    setDeleteDialogOpen(true);
  };

  const handleDeleteJob = async () => {
    if (!selectedJob) return;
    
    try {
      await api.delete(`/jobs/${selectedJob.id}`);
      toast.success("Job deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedJob(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete job");
    }
  };

  const handleCreateCustomer = async () => {
    if (!newCustomerData.name || !newCustomerData.address) {
      toast.error("Customer name and address are required");
      return;
    }
    
    setCreatingCustomer(true);
    try {
      const response = await api.post("/customers", newCustomerData);
      toast.success("Customer created successfully");
      
      // Add to customers list and select it
      setCustomers([...customers, response.data]);
      setFormData({
        ...formData,
        customer_id: response.data.id,
        delivery_address: response.data.address
      });
      
      setNewCustomerDialogOpen(false);
      setNewCustomerData({
        name: "",
        address: "",
        city: "",
        county: "",
        postcode: "",
        country: "",
        email: "",
        phone: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create customer");
    } finally {
      setCreatingCustomer(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedDriver) {
      toast.error("Please select a driver");
      return;
    }
    
    try {
      await api.put(`/jobs/${selectedJob.id}/assign?driver_id=${selectedDriver}`);
      toast.success("Driver assigned successfully");
      setAssignDialogOpen(false);
      setSelectedJob(null);
      setSelectedDriver("");
      fetchData();
    } catch (error) {
      toast.error("Failed to assign driver");
    }
  };

  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      delivery_address: customer?.address || ""
    });
  };

  const handleEditCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setEditFormData({
      ...editFormData,
      customer_id: customerId,
      delivery_address: customer?.address || editFormData.delivery_address
    });
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedJob) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    
    try {
      await api.post(`/jobs/${selectedJob.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success("Document uploaded successfully");
      fetchData();
      // Refresh selected job
      const updatedJob = jobs.find(j => j.id === selectedJob.id);
      if (updatedJob) {
        const res = await api.get(`/jobs/${selectedJob.id}`);
        setSelectedJob(res.data);
      }
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDocumentDownload = async (doc) => {
    try {
      const response = await api.get(`/jobs/${selectedJob.id}/documents/${doc.id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDocumentDelete = async (doc) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await api.delete(`/jobs/${selectedJob.id}/documents/${doc.id}`);
      toast.success("Document deleted");
      // Refresh selected job
      const res = await api.get(`/jobs/${selectedJob.id}`);
      setSelectedJob(res.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const openPhotosDialog = (job) => {
    setSelectedJob(job);
    setPhotosDialogOpen(true);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate it's an image
    if (!file.type.startsWith('image/')) {
      toast.error("Please select an image file");
      return;
    }
    
    const formData = new FormData();
    formData.append('file', file);
    
    setUploading(true);
    try {
      await api.post(`/jobs/${selectedJob.id}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Photo uploaded");
      const res = await api.get(`/jobs/${selectedJob.id}`);
      setSelectedJob(res.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to upload photo");
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handlePhotoDelete = async (photo) => {
    if (!confirm("Are you sure you want to delete this photo?")) return;
    
    try {
      await api.delete(`/jobs/${selectedJob.id}/photos/${photo.id}`);
      toast.success("Photo deleted");
      const res = await api.get(`/jobs/${selectedJob.id}`);
      setSelectedJob(res.data);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const toggleJobSelection = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedJobs.length === filteredJobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(filteredJobs.map(j => j.id));
    }
  };

  const handleExportPDF = async () => {
    if (selectedJobs.length === 0) {
      toast.error("Please select at least one job to export");
      return;
    }
    
    setExporting(true);
    try {
      const response = await api.post("/jobs/export-pdf", 
        { job_ids: selectedJobs },
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `jobs_export_${new Date().toISOString().slice(0,10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${selectedJobs.length} job(s) to PDF`);
      setSelectedJobs([]);
    } catch (error) {
      toast.error("Failed to export jobs");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.job_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.delivery_address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || job.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003366]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jobs-page">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#003366] text-white hidden lg:block">
        <div className="p-6">
          <img src={LOGO_URL} alt="Nassau Engineering" className="h-12 mb-6" />
          <p className="text-sm text-blue-200">Admin Dashboard</p>
        </div>
        
        <nav className="px-4 space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <BarChart3 className="h-5 w-5" />
            Dashboard
          </Link>
          <Link to="/admin/jobs" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-white/10 text-white">
            <Package className="h-5 w-5" />
            Delivery Jobs
          </Link>
          <Link to="/admin/customers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Users className="h-5 w-5" />
            Customers
          </Link>
          <Link to="/admin/drivers" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Truck className="h-5 w-5" />
            Drivers
          </Link>
          <Link to="/admin/upload" className="flex items-center gap-3 px-4 py-3 rounded-lg hover:bg-white/10 text-blue-100">
            <Upload className="h-5 w-5" />
            Import Data
          </Link>
        </nav>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass-header sticky top-0 z-40 border-b border-slate-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <span className="font-semibold">Delivery Jobs</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Jobs</h1>
              <p className="text-slate-600">{jobs.length} total jobs</p>
            </div>
            <div className="flex gap-3">
              {selectedJobs.length > 0 && (
                <Button
                  variant="outline"
                  onClick={handleExportPDF}
                  disabled={exporting}
                  className="border-emerald-500 text-emerald-600 hover:bg-emerald-50"
                  data-testid="export-pdf-btn"
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  {exporting ? "Exporting..." : `Export ${selectedJobs.length} to PDF`}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate("/admin/upload")}
              >
                <Upload className="h-4 w-4 mr-2" />
                Import
              </Button>
              <Button
                onClick={() => setDialogOpen(true)}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="add-job-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                New Job
              </Button>
            </div>
          </div>

          {/* Filters */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
                <div className="relative">
                  <Label className="text-xs text-slate-500 mb-1 block">Search</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      placeholder="Search jobs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 h-10"
                      data-testid="search-jobs"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-10" data-testid="status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Type</Label>
                  <Select value={jobTypeFilter} onValueChange={setJobTypeFilter}>
                    <SelectTrigger className="h-10" data-testid="type-filter">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="delivery">Delivery</SelectItem>
                      <SelectItem value="collection">Collection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Customer</Label>
                  <Select value={customerFilter} onValueChange={setCustomerFilter}>
                    <SelectTrigger className="h-10" data-testid="customer-filter">
                      <SelectValue placeholder="Customer" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Customers</SelectItem>
                      {customers.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">From Date</Label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-10"
                    data-testid="date-from"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">To Date</Label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-10"
                    data-testid="date-to"
                  />
                </div>
              </div>
              {(statusFilter !== "all" || jobTypeFilter !== "all" || customerFilter !== "all" || dateFrom || dateTo || searchTerm) && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-sm text-slate-600">
                    Showing {filteredJobs.length} of {jobs.length} jobs
                  </p>
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters">
                    Clear Filters
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Jobs List */}
          <div className="space-y-4">
            {/* Select All header */}
            {filteredJobs.length > 0 && (
              <div className="flex items-center gap-3 px-2">
                <Checkbox
                  checked={selectedJobs.length === filteredJobs.length && filteredJobs.length > 0}
                  onCheckedChange={toggleSelectAll}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-slate-600">
                  {selectedJobs.length > 0 
                    ? `${selectedJobs.length} selected` 
                    : "Select all"}
                </span>
              </div>
            )}
            
            {filteredJobs.map((job) => (
              <Card key={job.id} className={`card-nassau ${selectedJobs.includes(job.id) ? 'ring-2 ring-[#003366]' : ''}`} data-testid={`job-${job.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div className="flex gap-4 flex-1">
                      {/* Checkbox */}
                      <div className="flex items-start pt-1">
                        <Checkbox
                          checked={selectedJobs.includes(job.id)}
                          onCheckedChange={() => toggleJobSelection(job.id)}
                          data-testid={`select-job-${job.id}`}
                        />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-sm text-[#003366] font-medium">{job.job_number}</span>
                          <JobTypeBadge type={job.job_type || "delivery"} />
                          <StatusBadge status={job.status} />
                          {job.documents && job.documents.length > 0 && (
                            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                              <Paperclip className="h-3 w-3 mr-1" />
                              {job.documents.length} doc{job.documents.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-slate-900 text-lg">{job.customer_name}</h3>
                        <div className="space-y-1 mt-2 text-sm text-slate-600">
                          <div className="flex items-start gap-2">
                            <MapPin className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />
                            <span>{job.delivery_address}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4 text-slate-400" />
                              {job.delivery_date}
                            </span>
                            {job.time_window && <span>| {job.time_window}</span>}
                          </div>
                          <div className="flex items-center gap-1">
                            <Truck className="h-4 w-4 text-slate-400" />
                            <span>{job.assigned_driver_name || "Unassigned"}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(job)}
                        data-testid={`edit-${job.id}`}
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDocumentsDialog(job)}
                        data-testid={`docs-${job.id}`}
                      >
                        <FileText className="h-4 w-4 mr-2" />
                        Documents
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openPhotosDialog(job)}
                        data-testid={`photos-${job.id}`}
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Photos {job.photos?.length > 0 && `(${job.photos.length})`}
                      </Button>
                      {!job.assigned_driver_id && job.status === "pending" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedJob(job);
                            setAssignDialogOpen(true);
                          }}
                          data-testid={`assign-${job.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-2" />
                          Assign
                        </Button>
                      )}
                      {job.proof_id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/admin/docket/${job.proof_id}`)}
                          data-testid={`view-proof-${job.id}`}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Proof
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openDeleteDialog(job)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-${job.id}`}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredJobs.length === 0 && (
            <div className="text-center py-12">
              <Package className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No jobs found</h3>
              <p className="text-slate-500">Create a new job or import from Excel</p>
            </div>
          )}
        </div>
      </main>

      {/* Create Job Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Job</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Job Type *</Label>
              <Select value={formData.job_type} onValueChange={(v) => setFormData({ ...formData, job_type: v })}>
                <SelectTrigger data-testid="job-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Customer *</Label>
              <div className="flex gap-2">
                <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger data-testid="customer-select" className="flex-1">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setNewCustomerDialogOpen(true)}
                  data-testid="new-customer-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  New
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="address">{formData.job_type === "collection" ? "Collection" : "Delivery"} Address *</Label>
              <Input
                id="address"
                value={formData.delivery_address}
                onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                placeholder={formData.job_type === "collection" ? "Collection address" : "Full delivery address"}
                data-testid="job-address-input"
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">{formData.job_type === "collection" ? "Collection" : "Delivery"} Date *</Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.delivery_date}
                  onChange={(e) => setFormData({ ...formData, delivery_date: e.target.value })}
                  data-testid="job-date-input"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time Window</Label>
                <Input
                  id="time"
                  value={formData.time_window}
                  onChange={(e) => setFormData({ ...formData, time_window: e.target.value })}
                  placeholder="e.g., 9AM - 12PM"
                  data-testid="job-time-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Assign Driver</Label>
              <Select value={formData.assigned_driver_id} onValueChange={(v) => setFormData({ ...formData, assigned_driver_id: v })}>
                <SelectTrigger data-testid="driver-select">
                  <SelectValue placeholder="Select driver (optional)" />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="items">Items Description</Label>
              <Textarea
                id="items"
                value={formData.items_description}
                onChange={(e) => setFormData({ ...formData, items_description: e.target.value })}
                placeholder="What's being delivered...

Example:
- 2x Steel Beams (10ft)
- 1x Control Panel
- 5x Pipe Fittings"
                className="min-h-[150px] resize-y"
                data-testid="job-items-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="instructions">Special Instructions</Label>
              <Textarea
                id="instructions"
                value={formData.special_instructions}
                onChange={(e) => setFormData({ ...formData, special_instructions: e.target.value })}
                placeholder="Access codes, delivery notes, etc."
                data-testid="job-instructions-input"
              />
            </div>

            {/* Document Upload Section */}
            <div className="space-y-2">
              <Label>Attach Documents</Label>
              <div className="border-2 border-dashed border-slate-300 rounded-lg p-4">
                <input
                  ref={createJobFileInputRef}
                  type="file"
                  onChange={handlePendingDocumentAdd}
                  className="hidden"
                  id="create-job-doc-upload"
                  multiple
                  data-testid="create-job-doc-input"
                />
                <label htmlFor="create-job-doc-upload" className="cursor-pointer flex flex-col items-center">
                  <FileText className="h-8 w-8 text-slate-400 mb-2" />
                  <span className="text-sm text-slate-600">Click to add documents</span>
                </label>
              </div>
              {pendingDocuments.length > 0 && (
                <div className="space-y-2 mt-2">
                  {pendingDocuments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-700">{file.name}</span>
                        <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePendingDocument(index)}
                        className="text-red-600 hover:text-red-700 h-8 w-8 p-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="save-job-btn"
              >
                {submitting ? "Creating..." : "Create Job"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Job Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit {editFormData.job_type === "collection" ? "Collection" : "Delivery"} Job - {selectedJob?.job_number}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Job Type</Label>
              <Select value={editFormData.job_type} onValueChange={(v) => setEditFormData({ ...editFormData, job_type: v })}>
                <SelectTrigger data-testid="edit-job-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="delivery">Delivery</SelectItem>
                  <SelectItem value="collection">Collection</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select value={editFormData.customer_id} onValueChange={handleEditCustomerChange}>
                <SelectTrigger data-testid="edit-customer-select">
                  <SelectValue placeholder="Select customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-address">{editFormData.job_type === "collection" ? "Collection" : "Delivery"} Address</Label>
              <Input
                id="edit-address"
                value={editFormData.delivery_address}
                onChange={(e) => setEditFormData({ ...editFormData, delivery_address: e.target.value })}
                placeholder={editFormData.job_type === "collection" ? "Collection address" : "Full delivery address"}
                data-testid="edit-job-address-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit-date">{editFormData.job_type === "collection" ? "Collection" : "Delivery"} Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editFormData.delivery_date}
                  onChange={(e) => setEditFormData({ ...editFormData, delivery_date: e.target.value })}
                  data-testid="edit-job-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-time">Time Window</Label>
                <Input
                  id="edit-time"
                  value={editFormData.time_window}
                  onChange={(e) => setEditFormData({ ...editFormData, time_window: e.target.value })}
                  placeholder="e.g., 9AM - 12PM"
                  data-testid="edit-job-time-input"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Assign Driver</Label>
                <Select value={editFormData.assigned_driver_id} onValueChange={(v) => setEditFormData({ ...editFormData, assigned_driver_id: v })}>
                  <SelectTrigger data-testid="edit-driver-select">
                    <SelectValue placeholder="Select driver" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__unassigned__">Unassigned</SelectItem>
                    {drivers.map((d) => (
                      <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editFormData.status} onValueChange={(v) => setEditFormData({ ...editFormData, status: v })}>
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-items">Items Description</Label>
              <Textarea
                id="edit-items"
                value={editFormData.items_description}
                onChange={(e) => setEditFormData({ ...editFormData, items_description: e.target.value })}
                placeholder="What's being delivered..."
                className="min-h-[150px] resize-y"
                data-testid="edit-job-items-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="edit-instructions">Special Instructions</Label>
              <Textarea
                id="edit-instructions"
                value={editFormData.special_instructions}
                onChange={(e) => setEditFormData({ ...editFormData, special_instructions: e.target.value })}
                placeholder="Access codes, delivery notes, etc."
                data-testid="edit-job-instructions-input"
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#003366] hover:bg-[#002244]"
                data-testid="update-job-btn"
              >
                {submitting ? "Updating..." : "Update Job"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={documentsDialogOpen} onOpenChange={setDocumentsDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Documents - {selectedJob?.job_number}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Section */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleDocumentUpload}
                className="hidden"
                id="doc-upload"
                data-testid="doc-upload-input"
              />
              <label htmlFor="doc-upload" className="cursor-pointer">
                <FileText className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">Click to upload a document</p>
                <Button asChild variant="outline" size="sm" disabled={uploading}>
                  <span>{uploading ? "Uploading..." : "Choose File"}</span>
                </Button>
              </label>
            </div>

            {/* Documents List */}
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Attached Documents</h4>
              {(!selectedJob?.documents || selectedJob.documents.length === 0) ? (
                <p className="text-sm text-slate-500 py-4 text-center">No documents attached</p>
              ) : (
                <div className="space-y-2">
                  {selectedJob.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium text-slate-700">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            {(doc.size / 1024).toFixed(1)} KB • {doc.uploaded_by}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDocumentDownload(doc)}
                          data-testid={`download-doc-${doc.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDocumentDelete(doc)}
                          className="text-red-600 hover:text-red-700"
                          data-testid={`delete-doc-${doc.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDocumentsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photos Dialog */}
      <Dialog open={photosDialogOpen} onOpenChange={setPhotosDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Photos - {selectedJob?.job_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Upload Section */}
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
                id="photo-upload"
                data-testid="photo-upload-input"
              />
              <label htmlFor="photo-upload" className="cursor-pointer">
                <Camera className="h-10 w-10 mx-auto text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">Click to upload a photo</p>
                <Button asChild variant="outline" size="sm" disabled={uploading}>
                  <span>{uploading ? "Uploading..." : "Choose Photo"}</span>
                </Button>
              </label>
            </div>

            {/* Photos List */}
            <div className="space-y-2">
              <h4 className="font-medium text-slate-700">Uploaded Photos</h4>
              {(!selectedJob?.photos || selectedJob.photos.length === 0) ? (
                <p className="text-sm text-slate-500 py-4 text-center">No photos uploaded</p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {selectedJob.photos.map((photo) => (
                    <div key={photo.id} className="relative group">
                      <img
                        src={`data:${photo.content_type};base64,${photo.data}`}
                        alt={photo.filename}
                        className="w-full h-32 object-cover rounded-lg border border-slate-200"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handlePhotoDelete(photo)}
                          className="text-white hover:text-red-400 hover:bg-transparent"
                          data-testid={`delete-photo-${photo.id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 mt-1 truncate">{photo.uploaded_by}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPhotosDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Driver Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Driver</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Assign a driver to job <span className="font-mono font-medium">{selectedJob?.job_number}</span>
            </p>
            <Select value={selectedDriver} onValueChange={setSelectedDriver}>
              <SelectTrigger data-testid="assign-driver-select">
                <SelectValue placeholder="Select driver" />
              </SelectTrigger>
              <SelectContent>
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAssign}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="confirm-assign-btn"
            >
              Assign Driver
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete {selectedJob?.job_type === "collection" ? "Collection" : "Delivery"} Job
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-2">
              Are you sure you want to delete this {selectedJob?.job_type === "collection" ? "collection" : "delivery"} job?
            </p>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="font-mono text-sm text-[#003366] font-medium">{selectedJob?.job_number}</p>
              <p className="font-medium text-slate-900">{selectedJob?.customer_name}</p>
              <p className="text-sm text-slate-500">{selectedJob?.delivery_address}</p>
            </div>
            <p className="text-sm text-red-600 mt-3">
              This action cannot be undone. Any associated proof of {selectedJob?.job_type === "collection" ? "collection" : "delivery"} will also be deleted.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDeleteJob}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete Job
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New Customer Dialog */}
      <Dialog open={newCustomerDialogOpen} onOpenChange={setNewCustomerDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Customer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-2">
                <Label htmlFor="new-customer-name">Customer Name *</Label>
                <Input
                  id="new-customer-name"
                  value={newCustomerData.name}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, name: e.target.value })}
                  placeholder="Company or individual name"
                  data-testid="new-customer-name"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label htmlFor="new-customer-address">Address *</Label>
                <Input
                  id="new-customer-address"
                  value={newCustomerData.address}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, address: e.target.value })}
                  placeholder="Street address"
                  data-testid="new-customer-address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-city">Town/City</Label>
                <Input
                  id="new-customer-city"
                  value={newCustomerData.city}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, city: e.target.value })}
                  placeholder="Town/City"
                  data-testid="new-customer-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-county">County</Label>
                <Input
                  id="new-customer-county"
                  value={newCustomerData.county}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, county: e.target.value })}
                  placeholder="County"
                  data-testid="new-customer-county"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-postcode">Postal Code</Label>
                <Input
                  id="new-customer-postcode"
                  value={newCustomerData.postcode}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, postcode: e.target.value })}
                  placeholder="Postal Code"
                  data-testid="new-customer-postcode"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-country">Country</Label>
                <Input
                  id="new-customer-country"
                  value={newCustomerData.country}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, country: e.target.value })}
                  placeholder="Country"
                  data-testid="new-customer-country"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-email">Email</Label>
                <Input
                  id="new-customer-email"
                  type="email"
                  value={newCustomerData.email}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, email: e.target.value })}
                  placeholder="email@example.com"
                  data-testid="new-customer-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-customer-phone">Phone</Label>
                <Input
                  id="new-customer-phone"
                  value={newCustomerData.phone}
                  onChange={(e) => setNewCustomerData({ ...newCustomerData, phone: e.target.value })}
                  placeholder="+1234567890"
                  data-testid="new-customer-phone"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCustomerDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateCustomer}
              disabled={creatingCustomer}
              className="bg-[#003366] hover:bg-[#002244]"
              data-testid="save-new-customer-btn"
            >
              {creatingCustomer ? "Creating..." : "Create Customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile Navigation */}
      <nav className="lg:hidden mobile-nav bg-white border-t border-slate-200 h-16">
        <div className="h-full flex items-center justify-around">
          <Link to="/admin" className="flex flex-col items-center gap-1 text-slate-400">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">Dashboard</span>
          </Link>
          <Link to="/admin/jobs" className="flex flex-col items-center gap-1 text-[#003366]">
            <Package className="h-5 w-5" />
            <span className="text-xs">Jobs</span>
          </Link>
          <Link to="/admin/customers" className="flex flex-col items-center gap-1 text-slate-400">
            <Users className="h-5 w-5" />
            <span className="text-xs">Customers</span>
          </Link>
          <Link to="/admin/drivers" className="flex flex-col items-center gap-1 text-slate-400">
            <Truck className="h-5 w-5" />
            <span className="text-xs">Drivers</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}
